import React, { useEffect, useMemo, useState } from "react";
import { MuiButton, MuiSelector, MuiTab } from '@ace/ace-ui-web';
import { Box, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import { Groups2, CheckCircleOutline, HourglassTop } from '@mui/icons-material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useUserRole } from '../../hooks/useUserRole';
import { useProfileForm } from '../../hooks/useProfileForm';
import { useEmployeeSearch } from '../../hooks/useEmployeeSearch';
import { useUserStatus } from '../../hooks/useUserStatus';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { clearSearchedUser } from '../../redux/SearchedUserReducer/searchedUserSlice';
import ProfileInfoCard from "../../components/profile-info-card/ProfileInfoCard";
import SkillsCard from "../../components/skills-card/SkillsCard";
import { ProfilePageLayout } from "../../components/profile-page-layout/ProfilePageLayout";
import { EmployeeSearch } from "../../components/employee-search/EmployeeSearch";
import { getEmployeeStatusChip } from "../../utils/statusChipConfig";
import StatCard from '../../components/statcard/StatCard';
import { fetchProfileAdminSummary, PendingProfileUser } from '../../redux/ProfileAdminSummaryReducer/profileAdminSummarySlice';
import { fetchSupervisors } from '../../redux/SupervisorReducer/supervisorSlice';
import { Teams, fetchSkills as fetchTeams } from "../../redux/TeamReducer/teamSlice";

//Extract helper functions outside component
const getPageTitle = (isHROrManager: boolean) =>
    isHROrManager ? "Employee Profile" : "My Profile";

const getPageSubtitle = (isHROrManager: boolean) =>
    isHROrManager ? "Manage employee information and skills" : "View your profile and skills";

const getSkillsTitle = (isHROrManager: boolean) =>
    isHROrManager ? "Manage Skills" : "My Skills";

const getSkillsSubtitle = (isHROrManager: boolean) =>
    isHROrManager ? "Update employee skills" : "View your current skills";

//Extract loading spinner component
const LoadingSpinner: React.FC = () => (
    <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        className="spinner"
    />
);

// Extract loading state component
interface ProfileLoadingStateProps {
    title: string;
    subtitle: string;
    searchForm: React.ReactNode;
    loadingMessage: string;
}

const ProfileLoadingState: React.FC<ProfileLoadingStateProps> = ({
    title,
    subtitle,
    searchForm,
    loadingMessage,
}) => (
    <ProfilePageLayout
        title={title}
        subtitle={subtitle}
        searchForm={searchForm}
        loading={true}
        loadingMessage={loadingMessage}
    />
);

interface PendingUserTableRow {
    id: string;
    workdayId: string;
    fullName: string;
    status: string;
    skills: string[];
}

interface SelectorOption {
    label: string;
    value: string;
}

const ALL_SUPERVISORS_VALUE = '__ALL__';

interface ProfileAdminLandingStateProps {
    totalRegistered: number;
    activeUsers: number;
    pendingUsersCount: number;
    allUsers: PendingProfileUser[];
    loading: boolean;
    onSelectUser: (workdayId: string) => void;
    supervisorOptions: SelectorOption[];
    selectedSupervisorId: string;
    onSupervisorChange: (value: string) => void;
    supervisorsLoading: boolean;
    selectedTabIndex: number;
    onTabChange: (tabIndex: number) => void;
}

const ProfileAdminLandingState: React.FC<ProfileAdminLandingStateProps> = ({
    totalRegistered,
    activeUsers,
    pendingUsersCount,
    allUsers,
    loading,
    onSelectUser,
    supervisorOptions,
    selectedSupervisorId,
    onSupervisorChange,
    supervisorsLoading,
    selectedTabIndex,
    onTabChange,
}) => {
    // Map tab index to status value
    const tabStatusMap: Record<number, string> = {
        0: 'PND',  // Pending Users
        1: 'ACT',  // Active Users
    };
    const selectedStatus = tabStatusMap[selectedTabIndex];

    // Calculate pending direct reports (PND users for selected supervisor)
    const calculatedPendingDirectReports = useMemo(() => {
        if (!selectedSupervisorId || selectedSupervisorId === ALL_SUPERVISORS_VALUE) return 0;
        return allUsers.filter(
            (user: PendingProfileUser) => user.supervisorId === selectedSupervisorId && user.status === 'PND'
        ).length;
    }, [allUsers, selectedSupervisorId]);

    const rows = useMemo<PendingUserTableRow[]>(
        () => allUsers
            .filter((user) => user.status === selectedStatus)
            .map((user) => ({
                id: user.workdayId,
                workdayId: user.workdayId,
                fullName: user.fullName,
                status: user.status,
                skills: user.skills || [],
            })),
        [allUsers, selectedStatus]
    );

    const columns = useMemo<GridColDef[]>(
        () => [
            {
                field: 'workdayId',
                headerName: 'Workday ID',
                flex: 0.8,
                minWidth: 120,
            },
            {
                field: 'fullName',
                headerName: 'Full Name',
                flex: 1,
                minWidth: 180,
            },
            {
                field: 'skills',
                headerName: 'Skills',
                flex: 2,
                minWidth: 300,
                renderCell: (params: GridRenderCellParams) => {
                    const skills = params.value as string[] || [];
                    if (skills.length === 0) return <Typography variant="body2" sx={{ color: '#999', fontStyle: 'italic' }}>No skills</Typography>;
                    return (
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', py: 1 }}>
                            {skills.map((skill) => (
                                <Chip
                                    key={skill}
                                    label={skill}
                                    size="small"
                                    sx={{
                                        backgroundColor: '#D40029',
                                        color: 'white !important',
                                        fontWeight: 500,
                                        fontSize: '0.75rem',
                                        height: '22px',
                                        '& .MuiChip-label': {
                                            color: 'white !important',
                                        },
                                    }}
                                />
                            ))}
                        </Box>
                    );
                },
            },
            {
                field: 'status',
                headerName: 'Status',
                flex: 0.8,
                minWidth: 140,
                renderCell: (params: GridRenderCellParams) => getEmployeeStatusChip(String(params.value || 'PND')),
            },
        ],
        []
    );

    return (
        <Stack spacing={2.5} sx={{ mt: '16px', px: { xs: '8px', sm: '16px', md: '20px' } }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                {selectedSupervisorId !== ALL_SUPERVISORS_VALUE && (
                    <Box sx={{ flex: 1 }}>
                        <StatCard
                            value={calculatedPendingDirectReports}
                            title="Pending Direct Reports"
                            note={selectedSupervisorId ? `${supervisorOptions.find(option => option.value === selectedSupervisorId)?.label || 'Supervisor'}'s direct reports` : 'Pending direct reports'}
                            IconProp={<HourglassTop />}
                            colorProp="red"
                        />
                    </Box>
                )}
                <Box sx={{ flex: 1 }}>
                    <StatCard
                        value={pendingUsersCount}
                        title="Pending"
                        note="Total pending team members"
                        IconProp={<HourglassTop />}
                        colorProp="yellow"
                    />
                </Box>
                <Box sx={{ flex: 1 }}>
                    <StatCard
                        value={activeUsers}
                        title="Active"
                        note="Total active team members"
                        IconProp={<CheckCircleOutline />}
                        colorProp="green"
                    />
                </Box>
                <Box sx={{ flex: 1 }}>
                    <StatCard
                        value={totalRegistered}
                        title="Total"
                        note="Active and pending team members"
                        IconProp={<Groups2 />}
                        colorProp="lightblue"
                    />
                </Box>
            </Stack>

            <Box
                sx={{
                    backgroundColor: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '12px',
                    overflow: 'hidden',
                }}
            >
                <Box sx={{ 
                    p: { xs: 1.5, sm: 2 }, 
                    pb: 0,
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    justifyContent: 'space-between',
                    alignItems: { xs: 'flex-start', md: 'center' },
                    gap: { xs: 2, md: 0 },
                }}>
                    <MuiTab
                        fontSize="default"
                        fontWeight="default"
                        tabs={[
                            { label: 'Pending', content: '' },
                            { label: 'Active', content: '' }
                        ]}
                        value={selectedTabIndex}
                        onChange={onTabChange}
                        renderPanels={false}
                    />
                    <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                    >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Supervisor
                        </Typography>
                        <MuiSelector
                            value={selectedSupervisorId}
                            options={supervisorOptions}
                            onChange={onSupervisorChange}
                            disabled={supervisorsLoading}
                            placeholder="Select..."
                        />
                    </Stack>
                </Box>
                <Box sx={{ px: { xs: 1.5, sm: 2 }, pt: 0, pb: 0.75 }}>
                    <Typography variant="body2" sx={{ color: '#666' }}>
                        Click on a row to open that employee&apos;s profile
                    </Typography>
                </Box>
                <Box sx={{ px: { xs: 1.5, sm: 2 }, pt: 0, pb: { xs: 1.5, sm: 2 } }}>
                    {loading ? (
                        <Stack spacing={1.5} alignItems="center" justifyContent="center" sx={{ minHeight: 220 }}>
                            <CircularProgress size={30} />
                            <Typography variant="body2">Loading employee summary...</Typography>
                        </Stack>
                    ) : (
                        <DataGrid
                            rows={rows}
                            columns={columns}
                            autoHeight
                            pageSizeOptions={[5, 10, 25]}
                            initialState={{
                                pagination: {
                                    paginationModel: { pageSize: 10 },
                                },
                            }}
                            onRowClick={(params) => onSelectUser(String(params.row.workdayId))}
                            disableRowSelectionOnClick
                            getRowHeight={() => 'auto'}
                            sx={{
                                '& .MuiDataGrid-row': {
                                    cursor: 'pointer',
                                    minHeight: '60px !important',
                                },
                                '& .MuiDataGrid-cell': {
                                    display: 'flex',
                                    alignItems: 'center',
                                    paddingTop: '8px',
                                    paddingBottom: '8px',
                                },
                            }}
                        />
                    )}
                </Box>
            </Box>
        </Stack>
    );
};

// Extract ButtonRow component outside of ProfilePage
interface ButtonRowProps {
    showActivateButton: boolean;
    updating: boolean;
    statusActionLabel: string;
    handleToggleStatus: () => void;
    isHROrManager: boolean;
    hasSearched: boolean;
    formUpdating: boolean;
    hasTeamChanges: boolean;
    handleSaveChanges: () => void;
}

interface ProfileActionButtonProps {
    label: string;
    loading: boolean;
    disabled: boolean;
    onClick: () => void;
    colors?: "secondary";
}

const ProfileActionButton: React.FC<ProfileActionButtonProps> = ({
    label,
    loading,
    disabled,
    onClick,
    colors,
}) => (
    <Box sx={{ flex: 1 }}>
        <motion.div whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }} style={{ width: '100%' }}>
            <MuiButton
                size="large"
                colors={colors}
                onClick={onClick}
                disabled={disabled}
                sx={{ width: '100%' }}
            >
                {loading ? <LoadingSpinner /> : label}
            </MuiButton>
        </motion.div>
    </Box>
);

const ButtonRow: React.FC<ButtonRowProps> = ({
    showActivateButton,
    updating,
    statusActionLabel,
    handleToggleStatus,
    isHROrManager,
    hasSearched,
    formUpdating,
    hasTeamChanges,
    handleSaveChanges,
}) => (
    <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
        {showActivateButton && (
            <ProfileActionButton
                label={statusActionLabel}
                loading={updating}
                disabled={updating}
                onClick={handleToggleStatus}
                colors="secondary"
            />
        )}
        {isHROrManager && hasSearched && (
            <ProfileActionButton
                label="Save Changes"
                loading={formUpdating}
                disabled={formUpdating || !hasTeamChanges}
                onClick={handleSaveChanges}
            />
        )}
    </Stack>
);

const ProfilePage: React.FC = () => {
    const dispatch = useAppDispatch();
    const { user: currentUser } = useAppSelector((state) => state.currentUser);
    const { user: searchedUser, loading: profileLoading, error: profileError, updating, updateError } = useAppSelector((state) => state.searchedUser);
    const { skills, loading: skillsLoading, error: skillsError } = useAppSelector((state) => state.skills);
    const { supervisors, loading: supervisorsLoading } = useAppSelector((state) => state.supervisors);
    const { teams, loading: teamsLoading } = useAppSelector((state) => state.teams);
    const {
        counts: profileSummaryCounts,
        allUsers,
        loading: profileSummaryLoading,
        error: profileSummaryError,
    } = useAppSelector((state) => state.profileAdminSummary as any);
    const { isHROrManager } = useUserRole();
    const [selectedSupervisorId, setSelectedSupervisorId] = useState<string>('');
    const [selectedTabIndex, setSelectedTabIndex] = useState<number>(0);

    const { searchEmployeeId, setSearchEmployeeId, hasSearched, handleSearch, searchByEmployeeId, resetSearch } = useEmployeeSearch();

    // Use useMemo to reduce recalculation
    const displayUser = useMemo(
        () => (isHROrManager && hasSearched ? searchedUser : currentUser),
        [isHROrManager, hasSearched, searchedUser, currentUser]
    );

        const {
        workdayId,
        userName,
        setUserName,
        nameError,
        site,
        userEmail,
        email,
        setEmail,
        phone,
        handlePhoneChange,
        userSkills,
        setUserSkills,
        userTeam,
        setUserTeam,
        hasChanges,
        hasSkillChanges,
        updating: formUpdating,
        handleSaveChanges,
    } = useProfileForm({ isHROrManager, displayUser, searchedUser });

    const { effectiveStatus, statusActionLabel, handleToggleStatus } = useUserStatus(searchedUser, displayUser);

    // Calculate if team has changed
    const hasTeamChanges = useMemo(() => {
        // For HR/Manager viewing employee
        if (isHROrManager && hasSearched) {
            return hasChanges;
        }
        // For regular user viewing their own profile
        return hasChanges;
    }, [isHROrManager, hasSearched, hasChanges]);

    // Reset dashboard view when employee profile page is mounted/active with searched user
    // This handles the case where user clicks the same tab button while viewing a profile detail
    useEffect(() => {
        // Add a listener to detect when the sidebar button for employee profile is clicked
        const handleSidebarButtonClick = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            
            // Check if the click is within the sidebar
            const sidebar = target.closest('[class*="sidebar"]');
            if (!sidebar) {
                return; // Ignore clicks outside the sidebar
            }
            
            // Check if click is specifically on the EMPLOYEE PROFILE sidebar button
            const button = target.closest('button');
            if (!button) {
                return;
            }
            
            // More specific check - look for the exact menu button structure
            const menuButton = button.querySelector('[class*="menuButton"]');
            const buttonText = menuButton?.textContent || button.textContent || '';
            
            if (
                isHROrManager &&
                hasSearched &&
                buttonText.includes('EMPLOYEE PROFILE')
            ) {
                resetSearch();
            }
        };

        // Listen for clicks on the entire document
        document.addEventListener('click', handleSidebarButtonClick, true);

        return () => {
            document.removeEventListener('click', handleSidebarButtonClick, true);
        };
    }, [isHROrManager, hasSearched, resetSearch]);

    // Combine related useEffects
    useEffect(() => {
        // Cleanup on unmount
        return () => {
            if (isHROrManager) {
                dispatch(clearSearchedUser());
            }
        };
    }, [dispatch, isHROrManager]);

    useEffect(() => {
        // Handle all errors in one place
        const errors = [
            { condition: profileError, message: `Failed to load profile: ${profileError}` },
            { condition: skillsError, message: `Failed to load skills: ${skillsError}` },
            { condition: updateError, message: `Failed to update profile: ${updateError}` },
        ];

        errors.forEach(({ condition, message }) => {
            if (condition) toast.error(message);
        });
    }, [profileError, skillsError, updateError, profileSummaryError]);

    useEffect(() => {
        if (!isHROrManager || !currentUser?.siteCd || hasSearched) {
            return;
        }

        dispatch(fetchProfileAdminSummary({ siteCd: currentUser.siteCd }));
    }, [dispatch, isHROrManager, currentUser?.siteCd, hasSearched]);

    useEffect(() => {
        if (isHROrManager && currentUser?.siteCd) {
            dispatch(fetchSupervisors(currentUser.siteCd));
        }
    }, [dispatch, isHROrManager, currentUser?.siteCd]);

    useEffect(() => {
        if (currentUser?.siteCd) {
            dispatch(fetchTeams());
        }
    }, [dispatch, currentUser?.siteCd]);

    useEffect(() => {
        if (!selectedSupervisorId && currentUser?.workdayId) {
            setSelectedSupervisorId(currentUser.workdayId);
        }
    }, [selectedSupervisorId, currentUser?.workdayId]);

    // Memoize computed values
    const availableSkills = useMemo(
        () => {
            const mappedSkills = skills?.map((skill) => skill.lookupValueDesc) || [];
            console.log('🔍 Redux skills:', skills);
            console.log('🔍 Mapped availableSkills:', mappedSkills);
            return mappedSkills;
        },
        [skills]
    );

    // Add team options
    const teamOptions = useMemo(() => {
        return (teams || []).map((teamItem: Teams) => ({
            label: teamItem.lookupValueDesc,
            value: teamItem.lookupValueCd
        }));
    }, [teams]);

    // Check if the DISPLAYED user (not the viewer) is admin or superuser
    const isDisplayedUserAdminOrSuperUser = useMemo(() => {
        return displayUser?.role === 'Administrator' || displayUser?.role === 'SuperUser' || displayUser?.isSuperUser;
    }, [displayUser]);

    const supervisorOptions = useMemo<SelectorOption[]>(() => {
        if (!currentUser?.workdayId) return [];

        const currentUserLabel =
            `${currentUser.firstName ?? ''} ${currentUser.lastName ?? ''}`.trim() || currentUser.workdayId;

        const others = (supervisors || [])
            .filter((s) => s.workdayId !== currentUser.workdayId)
            .sort((a, b) =>
                `${a.firstName ?? ''} ${a.lastName ?? ''}`.localeCompare(
                    `${b.firstName ?? ''} ${b.lastName ?? ''}`
                )
            )
            .map((s) => ({
                label: `${s.firstName ?? ''} ${s.lastName ?? ''}`.trim(),
                value: s.workdayId,
            }));

        return [
            { label: 'All', value: ALL_SUPERVISORS_VALUE },
            { label: currentUserLabel, value: currentUser.workdayId },
            ...others,
        ];
    }, [supervisors, currentUser?.workdayId, currentUser?.firstName, currentUser?.lastName]);

    const searchForm = useMemo(
        () =>
            isHROrManager ? (
                <EmployeeSearch
                    searchValue={searchEmployeeId}
                    onSearchChange={setSearchEmployeeId}
                    onSearch={handleSearch}
                    disabled={profileLoading}
                />
            ) : null,
        [isHROrManager, searchEmployeeId, setSearchEmployeeId, handleSearch, profileLoading]
    );

    const handleSkillToggle = (skill: string) => {
        if (!isHROrManager) return;
        setUserSkills((prev) => (prev.includes(skill) ? prev.filter((s) => s !== skill) : [...prev, skill]));
    };

    const handleSelectPendingUser = (workdayId: string) => {
        searchByEmployeeId(workdayId);
    };

    const handleSupervisorChange = (value: string) => {
        setSelectedSupervisorId(value);
    };

    const handleTabChange = (value: number) => {
        setSelectedTabIndex(value);
    };

    // Filter users by selected supervisor (both pending and active)
    const filteredAllUsers = useMemo(() => {
        if (!selectedSupervisorId || selectedSupervisorId === ALL_SUPERVISORS_VALUE) return allUsers;
        return allUsers.filter((user: PendingProfileUser) => user.supervisorId === selectedSupervisorId);
    }, [allUsers, selectedSupervisorId]);

    // Simplify conditional rendering logic
    // Show dashboard when: no search has been done, OR a search completed with no matching user
    const shouldShowAdminLandingState = isHROrManager && (!hasSearched || (!searchedUser && !profileLoading));
    const shouldShowLoadingForHRSearch = profileLoading && hasSearched && isHROrManager;
    const shouldShowLoadingForRegularUser = !isHROrManager && !displayUser;

    //  Early returns with cleaner conditions
    if (shouldShowLoadingForHRSearch) {
        return (
            <ProfileLoadingState
                title="Employee Profile"
                subtitle="Search for an employee to view and manage their profile"
                searchForm={searchForm}
                loadingMessage="Loading employee profile..."
            />
        );
    }

    if (shouldShowAdminLandingState) {
        return (
            <ProfilePageLayout
                title="Employee Profile"
                subtitle="Manage employee information and skills"
                headerMarginBottom="5px"
                searchForm={searchForm}
            >
                <ProfileAdminLandingState
                    totalRegistered={profileSummaryCounts.totalRegistered}
                    activeUsers={profileSummaryCounts.activeUsers}
                    pendingUsersCount={profileSummaryCounts.pendingUsers}
                    allUsers={filteredAllUsers}
                    loading={profileSummaryLoading}
                    onSelectUser={handleSelectPendingUser}
                    supervisorOptions={supervisorOptions}
                    selectedSupervisorId={selectedSupervisorId}
                    onSupervisorChange={handleSupervisorChange}
                    supervisorsLoading={supervisorsLoading}
                    selectedTabIndex={selectedTabIndex}
                    onTabChange={handleTabChange}
                />
            </ProfilePageLayout>
        );
    }

    if (shouldShowLoadingForRegularUser) {
        return (
            <ProfileLoadingState
                title={getPageTitle(isHROrManager)}
                subtitle={getPageSubtitle(isHROrManager)}
                searchForm={searchForm}
                loadingMessage="Loading profile..."
            />
        );
    }

    // Simplified conditional rendering
    const showActivateButton = isHROrManager && !!searchedUser;

    return (
        <ProfilePageLayout
            title={getPageTitle(isHROrManager)}
            subtitle={getPageSubtitle(isHROrManager)}
            headerMarginBottom={isHROrManager ? '5px' : '30px'}
            searchForm={searchForm}
        >
            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2.5} sx={{ mt: '20px', px: { xs: '8px', sm: '16px', md: '20px' } }}>
                <ProfileInfoCard
                    workdayId={workdayId}
                    userName={userName}
                    setUserName={setUserName}
                    nameError={nameError}
                    userEmail={userEmail}
                    email={email}
                    setEmail={setEmail}
                    phone={phone}
                    onPhoneChange={handlePhoneChange}
                    site={site}
                    team={userTeam}
                    onTeamChange={setUserTeam}
                    teamOptions={teamOptions}
                    teamsLoading={teamsLoading}
                    showTeamDropdown={true}
                    isAdminOrSuperUser={isDisplayedUserAdminOrSuperUser}
                    statusChip={getEmployeeStatusChip(effectiveStatus)}
                    isReadOnly={isHROrManager}
                    hasChanges={hasTeamChanges}
                    updating={formUpdating}
                    onSave={handleSaveChanges}
                    showSaveButton={!isHROrManager}
                    additionalActions={ isHROrManager ? (
                        <ButtonRow
                            showActivateButton={showActivateButton}
                            updating={updating}
                            statusActionLabel={statusActionLabel}
                            handleToggleStatus={handleToggleStatus}
                            isHROrManager={isHROrManager}
                            hasSearched={hasSearched}
                            formUpdating={formUpdating}
                            hasTeamChanges={hasTeamChanges}
                            handleSaveChanges={handleSaveChanges}
                        />
                    ) : undefined}
                />

                <SkillsCard
                    title={getSkillsTitle(isHROrManager)}
                    subtitle={getSkillsSubtitle(isHROrManager)}
                    userSkills={userSkills}
                    availableSkills={availableSkills}
                    skillsLoading={skillsLoading}
                    isEditable={isHROrManager}
                    onSkillToggle={handleSkillToggle}
                    showSaveButton={isHROrManager}
                    hasSkillChanges={hasSkillChanges}
                    updating={formUpdating}
                    onSave={handleSaveChanges}
                />
            </Stack>
        </ProfilePageLayout>
    );
};

export default ProfilePage;